import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { AppAlert, AppAutocomplete, AppSingleSelect } from '@pro-sto/ui';
import {
  carsControllerGetMakes,
  carsControllerGetModelsByMakeId,
  adminSearchCars,
} from '@pro-sto/api-client';
import type { SelectOption } from '@pro-sto/ui';
import type { SelectOption as AutocompleteOption } from '@pro-sto/ui';
import type { CarSearchResultDto } from '@pro-sto/api-client';
import { useStores } from '@/hooks';

interface CarMake {
  id?: string;
  name?: string;
}

interface CarModel {
  id?: string;
  name?: string;
  markId?: string;
}

export interface CarFieldProps {
  /** Номер автомобиля */
  licensePlate: string;
  /** Callback при изменении номера автомобиля */
  onLicensePlateChange: (plate: string) => void;
  /** Выбранная марка */
  selectedMake: SelectOption | null;
  /** Callback при изменении марки */
  onMakeChange: (make: SelectOption | null) => void;
  /** Выбранная модель */
  selectedModel: SelectOption | null;
  /** Callback при изменении модели */
  onModelChange: (model: SelectOption | null) => void;
  /** Выбранная машина из автокомплита */
  selectedCar: CarSearchResultDto | null;
  /** Callback при выборе машины */
  onCarSelect: (car: CarSearchResultDto | null) => void;
  /** Callback при изменении марки/модели после автозаполнения */
  onCarDetailsModified: (modified: boolean) => void;
  /** UUID клиента для фильтрации поиска машин */
  clientUuid?: string;
  /** Список машин клиента (из ClientPhoneField) */
  clientCarsOptions?: AutocompleteOption[];
  /** Режим только чтения */
  disabled?: boolean;
}

export const CarField: React.FC<CarFieldProps> = ({
  licensePlate,
  onLicensePlateChange,
  selectedMake,
  onMakeChange,
  selectedModel,
  onModelChange,
  selectedCar,
  onCarSelect,
  onCarDetailsModified,
  clientUuid,
  clientCarsOptions = [],
  disabled = false,
}) => {
  const { toastStore } = useStores();

  // Внутреннее состояние
  const [makes, setMakes] = useState<CarMake[]>([]);
  const [models, setModels] = useState<CarModel[]>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [carAutocompleteValue, setCarAutocompleteValue] = useState<AutocompleteOption | undefined>();
  const [carConflict, setCarConflict] = useState<{
    dbMake: string;
    dbModel: string;
    enteredMake: string;
    enteredModel: string;
  } | null>(null);

  const originalCarDetails = useRef<{ make: string | null; model: string | null }>({ make: null, model: null });
  const userEnteredCarDetails = useRef<{ make: string | null; model: string | null }>({ make: null, model: null });

  // Синхронизация autocomplete value при изменении licensePlate извне (для edit mode)
  useEffect(() => {
    if (disabled && licensePlate) {
      setCarAutocompleteValue({ label: licensePlate, value: null, isCustom: true });
    }
  }, [disabled, licensePlate]);

  // Загрузка марок
  const loadMakes = useCallback(async () => {
    try {
      const response = await carsControllerGetMakes({ limit: 1000 });
      setMakes(response.data || []);
    } catch (error) {
      console.error('Failed to load car makes:', error);
      toastStore.showError('Не удалось загрузить марки автомобилей');
    }
  }, [toastStore]);

  // Загрузка моделей
  const loadModels = useCallback(async (makeId: string) => {
    setIsLoadingModels(true);
    try {
      const response = await carsControllerGetModelsByMakeId({ id: makeId, limit: 1000 });
      setModels(response.data || []);
    } catch (error) {
      console.error('Failed to load car models:', error);
      toastStore.showError('Не удалось загрузить модели автомобилей');
    } finally {
      setIsLoadingModels(false);
    }
  }, [toastStore]);

  // Загрузка марок при монтировании (если не disabled)
  useEffect(() => {
    if (!disabled && makes.length === 0) {
      loadMakes();
    }
  }, [disabled, makes.length, loadMakes]);

  // Загрузка моделей при смене марки
  useEffect(() => {
    if (disabled) return;

    if (selectedMake) {
      loadModels(selectedMake.value!.toString());
      onModelChange(null);

      // Проверяем, была ли изменена марка после автозаполнения
      if (originalCarDetails.current.make && originalCarDetails.current.make !== selectedMake.label) {
        onCarDetailsModified(true);
      }
    } else {
      setModels([]);
      onModelChange(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMake, loadModels, disabled]);

  // Предзаполнение модели при выборе авто из автокомплита
  useEffect(() => {
    if (selectedCar && models.length > 0 && selectedMake) {
      if (selectedMake.label === selectedCar.make) {
        const modelOption = models
          .filter(m => m.id && m.name && m.markId === selectedMake.value)
          .map(m => ({ label: m.name!, value: m.id! }))
          .find(m => m.label === selectedCar.model);

        if (modelOption) {
          onModelChange(modelOption);
        }
      }
    }
  }, [selectedCar, models, selectedMake, onModelChange]);

  // Отслеживание изменения модели после автозаполнения
  useEffect(() => {
    if (selectedModel && originalCarDetails.current.model && originalCarDetails.current.model !== selectedModel.label) {
      onCarDetailsModified(true);
    }
  }, [selectedModel, onCarDetailsModified]);

  // Опции для селектов
  const makeOptions: SelectOption[] = useMemo(() => {
    return makes
      .filter(make => make.id && make.name)
      .map(make => ({ label: make.name!, value: make.id! }));
  }, [makes]);

  const modelOptions: SelectOption[] = useMemo(() => {
    return models
      .filter(model => model.id && model.name)
      .map(model => ({ label: model.name!, value: model.id! }));
  }, [models]);

  // Поиск автомобилей по номеру
  const searchCars = useCallback(async (plateQuery: string): Promise<AutocompleteOption[]> => {
    if (plateQuery.length < 2) return [];

    try {
      const results = await adminSearchCars({
        licensePlate: plateQuery,
        clientUuid,
        limit: 10,
      });

      return results.map((car: CarSearchResultDto) => ({
        label: car.license_plate,
        value: car.uuid,
        isCustom: false,
        rawData: car,
      } as AutocompleteOption & { rawData: CarSearchResultDto }));
    } catch (error) {
      console.error('Failed to search cars:', error);
      toastStore.showError('Не удалось выполнить поиск автомобилей');
      return [];
    }
  }, [clientUuid, toastStore]);

  // Выбор автомобиля из автокомплита
  const handleCarSelect = useCallback((option: AutocompleteOption) => {
    setCarAutocompleteValue(option);

    if (option.isCustom || !option.value) {
      onCarSelect(null);
      onLicensePlateChange(option.label);
      return;
    }

    const carData = (option as AutocompleteOption & { rawData: CarSearchResultDto }).rawData;
    if (carData) {
      const enteredMake = userEnteredCarDetails.current.make;
      const enteredModel = userEnteredCarDetails.current.model;

      if (
        enteredMake && enteredModel &&
        (enteredMake.toLowerCase() !== carData.make.toLowerCase() ||
          enteredModel.toLowerCase() !== carData.model.toLowerCase())
      ) {
        setCarConflict({
          dbMake: carData.make,
          dbModel: carData.model,
          enteredMake,
          enteredModel,
        });
      } else {
        const makeOption = makeOptions.find(m => m.label === carData.make);
        if (makeOption) {
          onMakeChange(makeOption);
        }
      }

      onCarSelect(carData);
      onLicensePlateChange(carData.license_plate);

      originalCarDetails.current = { make: carData.make, model: carData.model };
      onCarDetailsModified(false);
    }
  }, [makeOptions, onCarSelect, onLicensePlateChange, onMakeChange, onCarDetailsModified]);

  const handleReplaceCarWithDb = useCallback(() => {
    if (carConflict) {
      const dbMakeOption = makeOptions.find(opt => opt.label === carConflict.dbMake);
      const dbModelOption = modelOptions.find(opt => opt.label === carConflict.dbModel);

      if (dbMakeOption) onMakeChange(dbMakeOption);
      if (dbModelOption) onModelChange(dbModelOption);

      setCarConflict(null);
      onCarDetailsModified(false);
    }
  }, [carConflict, makeOptions, modelOptions, onMakeChange, onModelChange, onCarDetailsModified]);

  const handleKeepEnteredCar = useCallback(() => {
    setCarConflict(null);
    onCarDetailsModified(true);
  }, [onCarDetailsModified]);

  return (
    <>
      {/* Номер автомобиля */}
      <div className="booking-form__field" data-testid="booking-car-plate-input">
        <AppAutocomplete
          label="Номер автомобиля"
          placeholder="A000AA 111"
          value={carAutocompleteValue}
          options={clientCarsOptions}
          onSearch={disabled ? undefined : searchCars}
          onChange={disabled ? undefined : handleCarSelect}
          minSearchLength={2}
          searchDebounce={300}
          disabled={disabled}
          renderOption={(option) => {
            const carData = (option as AutocompleteOption & { rawData?: CarSearchResultDto }).rawData;
            if (carData?.make && carData?.model) {
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', pointerEvents: 'none' }}>
                  <div style={{ fontWeight: 500 }}>{option.label}</div>
                  <div style={{ fontSize: '13px', color: '#666' }}>{carData.make} {carData.model}</div>
                </div>
              );
            }
            return option.label;
          }}
        />
      </div>

      {/* Марка и Модель */}
      <div className="booking-form__field-row">
        <div className="booking-form__field" data-testid="booking-make-select">
          <AppSingleSelect
            label="Выберите марку"
            placeholder="Марка"
            options={makeOptions}
            value={selectedMake}
            onChange={(value) => {
              onMakeChange(value);
              userEnteredCarDetails.current.make = value?.label || null;
            }}
            clearable
            disabled={disabled}
          />
        </div>

        <div className="booking-form__field" data-testid="booking-model-select">
          <AppSingleSelect
            label="Выберите модель"
            placeholder="Модель"
            options={modelOptions}
            value={selectedModel}
            onChange={(value) => {
              onModelChange(value);
              userEnteredCarDetails.current.model = value?.label || null;
            }}
            disabled={disabled || !selectedMake || isLoadingModels}
            clearable
          />
        </div>
      </div>

      {/* Алерт о конфликте марки/модели */}
      {!disabled && carConflict && (
        <AppAlert
          message={`Мы нашли автомобиль в базе с таким номером, но с другими маркой и моделью. Заменить на "${carConflict.dbMake} ${carConflict.dbModel}"?`}
          onConfirm={handleReplaceCarWithDb}
          onCancel={handleKeepEnteredCar}
          confirmText="Заменить"
          cancelText="Оставить"
        />
      )}
    </>
  );
};
